import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  title: "",
  title_en: "",
  subtitle: "",
  subtitle_en: "",
  body: "",
  body_en: "",
  image: "",
  in_menu: true,
  sort_order: 0,
};

function SectionForm({ open, onOpenChange, section, onSaved }) {
  const { t } = useLanguage();
  const s = t.admin.section;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (section) setForm({ ...EMPTY, ...section });
    else setForm({ ...EMPTY });
  }, [section, open]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      if (section?.id) {
        await api.put(`/admin/sections/${section.id}`, form);
      } else {
        await api.post("/admin/sections", form);
      }
      toast.success(s.saved);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "title", label: t.admin.title, required: true },
    { key: "title_en", label: t.admin.titleEn },
    { key: "subtitle", label: s.subtitle },
    { key: "subtitle_en", label: s.subtitleEn },
    { key: "image", label: s.image },
    { key: "body", label: s.body, type: "textarea", rows: 7, required: true },
    { key: "body_en", label: s.bodyEn, type: "textarea", rows: 5 },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{section ? s.edit : s.new}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
              {f.type === "textarea" ? (
                <Textarea
                  data-testid={`section-form-${f.key}`}
                  rows={f.rows || 3}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={set(f.key)}
                  className="mt-1.5"
                />
              ) : (
                <Input
                  data-testid={`section-form-${f.key}`}
                  type={f.type || "text"}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={set(f.key)}
                  className="mt-1.5"
                />
              )}
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              data-testid="section-form-in_menu"
              type="checkbox"
              checked={form.in_menu}
              onChange={(e) => setForm({ ...form, in_menu: e.target.checked })}
              className="h-4 w-4 accent-[#2D4A3E]"
            />
            {s.inMenu}
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" data-testid="section-form-cancel" onClick={() => onOpenChange(false)}>
              {t.admin.cancel}
            </Button>
            <Button data-testid="section-form-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-dark">
              {t.admin.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSections() {
  const { t } = useLanguage();
  const s = t.admin.section;
  const [sections, setSections] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const refresh = () =>
    api.get("/admin/sections").then((res) => setSections(res.data)).catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const moveSection = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSections(next);
    try {
      await api.post("/admin/sections/reorder", { order: next.map((x) => x.id) });
      toast.success("Ordine aggiornato.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      refresh();
    }
  };

  const remove = async (sec) => {
    if (!window.confirm(s.confirmDelete)) return;
    await api.delete(`/admin/sections/${sec.id}`).catch(() => {});
    setSections(sections.filter((x) => x.id !== sec.id));
    toast.success(s.deleted);
  };

  return (
    <div>
      <div className="flex justify-end mt-8">
        <Button
          data-testid="admin-new-section-button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="gap-2 bg-brand hover:bg-brand-dark"
        >
          <Plus className="h-4 w-4" /> {s.new}
        </Button>
      </div>

      <div className="mt-4 space-y-3">
        <p className="text-xs font-light text-slate-400">Trascina le sezioni per riordinarle nel menu / Drag sections to reorder</p>
        {sections.map((sec) => (
          <div
            key={sec.id}
            data-testid="admin-section-row"
            draggable
            onDragStart={(e) => {
              setDragIndex(sections.findIndex((x) => x.id === sec.id));
              e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              moveSection(dragIndex, sections.findIndex((x) => x.id === sec.id));
            }}
            onDragEnd={() => setDragIndex(null)}
            className={`flex items-center justify-between gap-4 bg-white border border-slate-200 p-5 cursor-grab active:cursor-grabbing ${
              dragIndex === sections.findIndex((x) => x.id === sec.id) ? "opacity-50" : ""
            }`}
          >
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-brand">
                {sec.in_menu ? "Nel menu / In menu" : "Nascosta / Hidden"}
                {sec.sort_order ? ` · ${sec.sort_order}` : ""}
              </p>
              <p className="mt-1 font-serif text-lg text-slate-900 truncate">{sec.title}</p>
              <p className="text-xs font-light text-slate-400">/sezione/{sec.slug}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                data-testid={`admin-edit-section-${sec.slug}`}
                onClick={() => {
                  setEditing(sec);
                  setFormOpen(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                data-testid={`admin-delete-section-${sec.slug}`}
                onClick={() => remove(sec)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <p className="text-sm font-light text-slate-500">{s.empty}</p>
        )}
      </div>

      <SectionForm open={formOpen} onOpenChange={setFormOpen} section={editing} onSaved={refresh} />
    </div>
  );
}
